/* -----------------Globals--------------- */

/* -----------------Types--------------- */
import type { MixedItemTag } from "@memorang/types";

/* -----------------Components--------------- */
import { skillIconMap } from "../../constants";
import type { FontSchemaType } from "../../font";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { FlatCard } from "../Cards/FlatCard";
import { CustomListSubheader } from "../CustomListSubheader";
import { MasteryListItem } from "../MasteryListItem";
import { PremiumChip, type PremiumChipProps } from "../PremiumChip";

export type SectionListTag = MixedItemTag & {
	donutScore?: number;
	donutColorScale?: string[];
	titleChip?: string;
};

export type SectionTag = {
	title: string;
	tags: SectionListTag[];
	chipVariant?: PremiumChipProps["variant"];
	action?: () => void;
};

type Props = {
	sectionTags: SectionTag[];
	handleClickListItem: (selectedTagId: number, locked?: boolean) => void;
	appFont?: FontSchemaType;
};

const TagsSectionList = (props: Props) => {
	const { sectionTags, handleClickListItem, appFont } = props;
	const { isMobile } = useDevice();

	const renderSectionHeader = ({
		section: { title, chipVariant, action },
	}: {
		section: SectionTag;
	}) => (
		<Box
			justifyContent="space-between"
			display="flex"
			flexDirection="row"
			alignItems="center"
			paddingHorizontal={isMobile ? 16 : 0}
		>
			<CustomListSubheader enableGutters={false} font={appFont}>
				{title}
			</CustomListSubheader>
			{chipVariant ? (
				<PremiumChip variant={chipVariant} onPress={action} />
			) : null}
		</Box>
	);

	const getTagIcon = (tagTitle: string, sectionTitle: string) =>
		skillIconMap[tagTitle] ?? skillIconMap[sectionTitle];

	// Parent ScrollContainer owns scrolling — avoid nested virtualized lists.
	return (
		<Box gap={16} paddingTop={isMobile ? 8 : 16} paddingBottom={24}>
			{sectionTags.map((section, index) => (
				<Box gap={8} key={`${section.chipVariant ?? "default"}-${index}`}>
					{renderSectionHeader({ section })}
					<FlatCard
						style={{ overflow: "hidden", ...(isMobile && { borderRadius: 0 }) }}
					>
						{section.tags.map(
							({
								id,
								title,
								titleChip,
								locked,
								distribution,
								description,
								donutScore,
								numItems,
								donutColorScale,
							}) => (
								<MasteryListItem
									key={id}
									title={title}
									titleChip={titleChip}
									description={description}
									locked={locked}
									distribution={distribution}
									score={donutScore}
									numItems={numItems}
									colorScale={donutColorScale}
									icon={getTagIcon(title, section.title)}
									onPress={() => handleClickListItem(id, locked)}
								/>
							),
						)}
					</FlatCard>
				</Box>
			))}
		</Box>
	);
};

export default TagsSectionList;
